import { useEffect } from 'react';
import useWebSocket from 'react-use-websocket';

import { webSocketInstalledProducts } from '../solaris-lens-common-lib/src/constants/endpoints';
import { setSubscriptionMessage } from '../solaris-lens-common-lib/src/utils/webSocket';

export interface WebSocketProps {
  link?: string;
  options?: any;
  connectWs?: boolean;
  entityName?: string;
  entityId?: string;
  params?: string;
  onLastWsMessage?: (data: any) => void;
  binaryType?: string;
  args?: any[];
}

const useSocket = ({
  link = webSocketInstalledProducts,
  options,
  connectWs = false,
  entityName = '',
  entityId = '',
  params = '',
  onLastWsMessage = () => {},
  binaryType = 'blob',
  args = []
}: WebSocketProps) => {
  const useWebSocketInfo = useWebSocket(
    link,
    {
      share: false,
      shouldReconnect: () => false,
      reconnectAttempts: 3,
      reconnectInterval: 3000,
      ...options
    },
    connectWs
  );

  const { sendJsonMessage, lastMessage, readyState, getWebSocket } =
    useWebSocketInfo;

  useEffect(() => {
    if (entityName === '' || entityId === '') return;
    if (readyState !== WebSocket.OPEN || !connectWs) return;

    const ws = getWebSocket();
    if (ws && 'binaryType' in ws) {
      (ws as WebSocket).binaryType = binaryType as BinaryType;
    }

    sendJsonMessage(
      setSubscriptionMessage({
        entityName,
        entityId,
        params,
        args: args as never[]
      })
    );
  }, [readyState, connectWs, entityName, entityId]);

  useEffect(() => {
    const ws = getWebSocket();
    if (!ws) return;

    if (!connectWs && ws.readyState === WebSocket.OPEN) {
      ws.close();
    }
  }, [connectWs]);

  useEffect(() => {
    if (!lastMessage?.data) return;

    let cancelled = false;

    const deliverMessage = (json: unknown) => {
      if (cancelled) return;
      const payload = json as { _meta?: { class?: string } };
      if (payload?._meta?.class === entityName) {
        onLastWsMessage(json);
      }
    };

    const parseMessage = async () => {
      try {
        const raw = lastMessage.data;

        if (typeof raw === 'string') {
          deliverMessage(JSON.parse(raw));
          return;
        }

        if (raw instanceof Blob) {
          deliverMessage(JSON.parse(await raw.text()));
          return;
        }

        console.error('Bad WS message: unsupported data type');
      } catch (e) {
        console.error('Bad WS message', e);
      }
    };

    parseMessage();

    return () => {
      cancelled = true;
    };
  }, [lastMessage, entityId, entityName, connectWs]);

  return useWebSocketInfo;
};

export default useSocket;
